const env = import.meta.env

/**
 * Per-environment Auth0 tenant + API endpoints. Each env has its OWN Auth0
 * tenant, and an API audience only exists in its own tenant — so the domain +
 * clientId must switch with the environment, not just the audience. (Sending the
 * prod audience to the staging tenant yields "Service not found".)
 */
const ENVS = {
  staging: {
    auth0Domain: 'dev-tqo515nfomhzsx17.us.auth0.com',
    auth0ClientId: 'XvisxXYFgHy1wX5Y03tTccJsu7pyEeWn', // Nova's public staging SPA client
    auth0Audience: 'https://api.staging.zunou.ai/',
    graphqlUrl: 'https://api.staging.zunou.ai/graphql',
    pusherKey: '63cb68ba64a7e5a889ab',
    pusherAuthEndpoint: 'https://api.staging.zunou.ai/api/broadcast/auth',
    // Notification hub Lambda — client-triggered typing + reactions fan out
    // here (messages are ALSO server-triggered; see the API listener). Same
    // URLs the dashboard uses (glass-office shares its Auth0 client + API).
    notificationHubUrl: 'https://zr5v3zg4cnm2d2h2sb6cv34isa0xsork.lambda-url.ap-northeast-3.on.aws',
  },
  prod: {
    // The vanity login domain (auth.zunou.ai) — users see Zunou in the URL
    // bar during sign-in, not auth0.com. Tokens carry the custom-domain
    // issuer; every validator accepts both (roadmap B2).
    auth0Domain: 'auth.zunou.ai',
    auth0ClientId: 'evW6xc4M797Ui68o8lBm1sCbbTNHMAky', // prod public SPA client (same one dashboard.zunou.ai uses)
    auth0Audience: 'https://api.zunou.ai/',
    graphqlUrl: 'https://api.zunou.ai/graphql',
    pusherKey: 'a132c265423d2cb49ff0',
    pusherAuthEndpoint: 'https://api.zunou.ai/api/broadcast/auth',
    notificationHubUrl: 'https://kntj6cgnsjlp557zxbvsnzqvsa0jkyfn.lambda-url.ap-northeast-3.on.aws',
  },
} as const

export type EnvName = keyof typeof ENVS

const KEY_ENV = 'glass-office.env'
const KEY_TOKEN = 'glass-office.token'

function readStored(key: string): string {
  try {
    return (typeof localStorage !== 'undefined' && localStorage.getItem(key)) || ''
  } catch {
    return ''
  }
}

export function getEnvName(): EnvName {
  // URL param wins and is NOT persisted — the embedding dashboard pins its own
  // environment per visit without disturbing the user's standalone choice.
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('env')
    if (fromUrl === 'prod' || fromUrl === 'staging') return fromUrl
  } catch {
    // no window/URL — fall through
  }
  const stored = readStored(KEY_ENV)
  if (stored === 'prod' || stored === 'staging') return stored
  // No explicit choice yet: default to PRODUCTION on the deployed site, but to
  // staging for local dev — only localhost is a registered staging Auth0
  // callback, and the dev token targets staging.
  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  return /^(localhost|127\.0\.0\.1|\[::1\])$/.test(host) ? 'staging' : 'prod'
}
export function setEnvName(name: EnvName): void {
  try {
    localStorage.setItem(KEY_ENV, name)
  } catch {
    // ignore storage errors
  }
}

/** A pasted JWT (or build-time VITE_DEV_AUTH_TOKEN) that bypasses Auth0. */
export function getStoredToken(): string {
  return (env.VITE_DEV_AUTH_TOKEN ?? '').trim() || readStored(KEY_TOKEN).trim()
}
export function setStoredToken(token: string): void {
  try {
    if (token) localStorage.setItem(KEY_TOKEN, token)
    else localStorage.removeItem(KEY_TOKEN)
  } catch {
    // ignore storage errors
  }
}

const KEY_LIVE_ONLY = 'glass-office.liveOnly'
/** "Live only" mode shows ONLY real Pusher activity — no ambient simulation. */
export function getLiveOnly(): boolean {
  // Live is the DEFAULT mode — only an explicit Showcase choice is persisted.
  return readStored(KEY_LIVE_ONLY) !== '0'
}
export function setLiveOnly(on: boolean): void {
  try {
    // Live = default (clear the override); Showcase = an explicit stored choice.
    if (on) localStorage.removeItem(KEY_LIVE_ONLY)
    else localStorage.setItem(KEY_LIVE_ONLY, '0')
  } catch {
    // ignore storage errors
  }
}

const active = ENVS[getEnvName()]

/**
 * Staging defaults are baked in so the app runs on http://localhost:8080 with no
 * setup. The Auth0 client is Nova's PUBLIC SPA client (whitelists :8080).
 */
export const config = {
  auth0Domain: env.VITE_AUTH0_DOMAIN ?? active.auth0Domain,
  auth0ClientId: env.VITE_AUTH0_CLIENT_ID ?? active.auth0ClientId,
  auth0Audience: env.VITE_AUTH0_AUDIENCE ?? active.auth0Audience,
  graphqlUrl: env.VITE_CORE_GRAPHQL_URL ?? active.graphqlUrl,
  pusherKey: env.VITE_PUSHER_KEY ?? active.pusherKey,
  pusherCluster: env.VITE_PUSHER_CLUSTER ?? 'ap3',
  pusherAuthEndpoint: env.VITE_PUSHER_AUTH_ENDPOINT ?? active.pusherAuthEndpoint,
  notificationHubUrl: env.VITE_NOTIFICATION_HUB_URL ?? active.notificationHubUrl,
  envName: getEnvName(),
  devAuthToken: getStoredToken(),
} as const

export type GetToken = () => Promise<string | null>
